import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { Card, Spinner, useToast } from './ui';
import type { ExclusionScope, Language, Member } from '../types';

const T = {
  en: {
    title: 'What people will not eat',
    intro:
      'A hard no, not a preference — the planner treats these as rules, so use them for the real ones.',
    scopes:
      'Two kinds. “Never” keeps the ingredient out of anything that person eats. “Not on its own” only rules out dishes where it is obviously the thing on the plate — no pot of natural yoghurt, but the curry it was marinated in is fine.',
    never: 'Never',
    standalone: 'Not on its own',
    scopeHint: 'Which kind of no is this?',
    switched: (name: string, scope: string) => `${name}: ${scope}`,
    search: 'Search ingredients…',
    add: 'Add',
    none: 'Nothing listed — everybody eats everything.',
    remove: 'Remove',
    added: (name: string, who: string) => `${who} will not be served ${name}`,
    removed: 'Removed',
    noMatch: 'No ingredient matches that.',
    pick: 'Who?',
  },
  fr: {
    title: 'Ce que les gens ne mangent pas',
    intro:
      'Un vrai refus, pas une préférence — le planificateur en fait une règle.',
    scopes:
      'Deux formes. « Jamais » exclut l’ingrédient de tout ce que la personne mange. « Pas tout seul » n’exclut que les plats où c’est manifestement l’élément principal — pas de yaourt nature en pot, mais le curry mariné au yaourt passe très bien.',
    never: 'Jamais',
    standalone: 'Pas tout seul',
    scopeHint: 'Quel type de refus ?',
    switched: (name: string, scope: string) => `${name} : ${scope}`,
    search: 'Rechercher un ingrédient…',
    add: 'Ajouter',
    none: 'Rien pour le moment — tout le monde mange de tout.',
    remove: 'Retirer',
    added: (name: string, who: string) => `${who} ne mangera pas : ${name}`,
    removed: 'Retiré',
    noMatch: 'Aucun ingrédient ne correspond.',
    pick: 'Qui ?',
  },
} as const;

/**
 * Hard ingredient exclusions (§6, §20).
 *
 * The planning engine has always enforced these; there was simply no way to
 * say one. Deliberately framed as "will not eat" rather than "dislikes",
 * because a soft dislike belongs in scoring and this is a hard filter: a meal
 * containing it is removed from consideration entirely for anyone at the table
 * who has it listed.
 */
export function DislikesCard({ language, readOnly }: { language: Language; readOnly: boolean }) {
  const t = T[language];
  const toast = useToast();
  const [members, setMembers] = useState<Member[] | null>(null);
  const [ingredients, setIngredients] = useState<Array<{ id: string; name: string }>>([]);
  const [exclusions, setExclusions] = useState<Map<string, Map<string, ExclusionScope>>>(
    new Map(),
  );
  const [memberId, setMemberId] = useState<string>('');
  // What a newly added dislike means. 'never' is the stricter reading, so it
  // is the one that has to be chosen away from rather than into.
  const [scope, setScope] = useState<ExclusionScope>('never');
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [household, library, current] = await Promise.all([
      api.household(),
      api.ingredients(),
      api.exclusions(),
    ]);
    setMembers(household.members);
    setIngredients(library);
    setExclusions(
      new Map(
        household.members.map((m) => [
          m.id,
          new Map(
            current.filter((e) => e.memberId === m.id).map((e) => [e.ingredientId, e.scope]),
          ),
        ]),
      ),
    );
    setMemberId((existing) => existing || household.members[0]?.id || '');
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const nameOf = useMemo(
    () => (id: string) => ingredients.find((i) => i.id === id)?.name ?? id,
    [ingredients],
  );

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const already = exclusions.get(memberId) ?? new Map();
    return ingredients
      .filter((i) => i.name.toLowerCase().includes(q) && !already.has(i.id))
      .slice(0, 6);
  }, [query, ingredients, exclusions, memberId]);

  if (!members) {
    return (
      <Card title={t.title}>
        <Spinner />
      </Card>
    );
  }

  const set = async (
    ingredientId: string,
    excluded: boolean,
    how: ExclusionScope = scope,
    forMember: string = memberId,
  ) => {
    setBusy(true);
    try {
      await api.setExclusion(forMember, ingredientId, excluded, how);
      await load();
      const who = members.find((m) => m.id === forMember)?.name ?? '';
      toast(excluded ? t.added(nameOf(ingredientId), who) : t.removed);
      setQuery('');
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  const anyListed = [...exclusions.values()].some((listed) => listed.size > 0);

  return (
    <Card title={t.title}>
      <p className="dim" style={{ marginBottom: 6 }}>
        {t.intro}
      </p>
      <p className="dim" style={{ marginBottom: 14 }}>
        {t.scopes}
      </p>

      {!anyListed && <p className="dim">{t.none}</p>}

      <div className="stack" style={{ gap: 10 }}>
        {members.map((member) => {
          const listed = [...(exclusions.get(member.id) ?? new Map<string, ExclusionScope>())];
          if (listed.length === 0) return null;
          return (
            <div key={member.id} className="access-row" style={{ alignItems: 'flex-start' }}>
              <span className="access-email" style={{ flex: '0 0 110px' }}>
                {member.name}
              </span>
              <span className="chips" style={{ flex: 1 }}>
                {listed.map(([id, how]) => (
                  <span key={id} className="chip scoped">
                    <span className="chip-name">{nameOf(id)}</span>
                    <button
                      type="button"
                      className={`chip-scope${how === 'never' ? ' hard' : ''}`}
                      disabled={readOnly || busy}
                      title={t.scopeHint}
                      onClick={() =>
                        void set(id, true, how === 'never' ? 'standalone' : 'never', member.id)
                      }
                    >
                      {how === 'never' ? t.never : t.standalone}
                    </button>
                    <button
                      type="button"
                      className="chip-remove"
                      disabled={readOnly || busy}
                      title={t.remove}
                      onClick={() => void set(id, false, how, member.id)}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </span>
            </div>
          );
        })}
      </div>

      {!readOnly && (
        <div className="row" style={{ gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
          <label className="row" style={{ gap: 8 }}>
            <span className="dim">{t.pick}</span>
            <select value={memberId} onChange={(event) => setMemberId(event.target.value)} disabled={busy}>
              {members.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.name}
                </option>
              ))}
            </select>
          </label>
          <span className="row" style={{ gap: 6 }}>
            {(
              [
                ['never', t.never],
                ['standalone', t.standalone],
              ] as Array<[ExclusionScope, string]>
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={`btn small${scope === value ? ' on' : ''}`}
                aria-pressed={scope === value}
                disabled={busy}
                onClick={() => setScope(value)}
              >
                {label}
              </button>
            ))}
          </span>
          <input
            type="search"
            value={query}
            placeholder={t.search}
            disabled={busy}
            style={{ flex: 1, minWidth: 180 }}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
      )}

      {!readOnly && query.trim() && (
        <div className="row" style={{ gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          {matches.map((option) => (
            <button
              key={option.id}
              className="btn small"
              disabled={busy}
              onClick={() => void set(option.id, true)}
            >
              + {option.name}
            </button>
          ))}
          {matches.length === 0 && <p className="dim">{t.noMatch}</p>}
        </div>
      )}
    </Card>
  );
}
